import { state } from "../state.js";
import { getSetting } from "../settings.js";
import { createTab } from "../dom/layout.js";
import { renderHead, renderRows } from "../dom/table.js";

function rankCol(header, key) {
  return { header, headClass: "rankHead", key, cellClass: "rankRow" };
}

// A non-breaking space instead of "" keeps an empty stacked line at normal
// line-height (an empty <div> collapses to zero height), so a fixed second
// line (e.g. driver name below Club, or Sponsor below Fahrzeug) stays at the
// same vertical position across rows regardless of whether the top slot has
// text for that particular row.
function orPlaceholder(text) {
  return text || " ";
}

function timeCol(header, key) {
  return { header, headClass: "timeHead", key, cellClass: "timeRow" };
}

function penaltyCol(key, header = "STRAFE") {
  return { header, headClass: "penaltyHeader", key, cellClass: "penaltyRow" };
}

// One column per lap: time on top, penalty stacked below it (omitted when
// there is none), instead of two separate TIME/STRAFE columns.
function timePenaltyCol(header, timeKey, penaltyKey) {
  return {
    header,
    headClass: "timeHead",
    cellClass: "timeRow",
    renderParts: (row) => {
      const parts = [{ text: row[timeKey], className: "timeValue" }];
      if (row[penaltyKey]) parts.push({ text: row[penaltyKey], className: "penaltyValue" });
      return parts;
    },
  };
}

// POS / POS CL / POS GRP: which columns appear, and which field feeds the
// first ("POS") column, depends on the tab's RankingType.
function buildPosColumns(tableEntry) {
  if (tableEntry.Ranking === "Grid") return [];
  switch (tableEntry.RankingType) {
    case "ALL": {
      const cols = [rankCol("POS", "Pos"), rankCol("POS CL", "PosC")];
      if (state.displayGroup) cols.push(rankCol("POS GRP", "PosG"));
      return cols;
    }
    case "Group":
      return [rankCol("POS", "PosG"), rankCol("POS CL", "PosC")];
    default:
      return [rankCol("POS", "PosC")];
  }
}

function buildNameColumns(tableEntry) {
  const cols = [{ header: "NO", headClass: "stnHead", key: "STN", cellClass: "stnRow" }];
  if (state.displayGuest) {
    cols.push({
      header: "G",
      headClass: "guestHead",
      cellClass: "stnRow",
      render: (row) => (row.Guest ? "G" : ""),
    });
  }
  cols.push(buildNameColumn());
  if (["ALL", "Group"].includes(tableEntry.RankingType)) {
    cols.push({ header: "Klasse", headClass: "groupHead", key: "Class", cellClass: "nameRow" });
  }
  if (tableEntry.RankingType === "ALL" && state.displayGroup) {
    cols.push({ header: "GRUPPE", headClass: "groupHead", key: "Group", cellClass: "nameRow" });
  }
  cols.push(...buildVehicleSponsorColumn());
  return cols;
}

// Driver name, with an optional Club (Competitor) line stacked above it -
// toggled via config.json "showClub", with a configurable "clubLabel" alias
// for the header.
function buildNameColumn() {
  const nameValue = (row) => `${row.names.drivers[0].name}, ${row.names.drivers[0].firstname}`;
  if (!getSetting("showClub")) {
    return { header: "NAME", headClass: "nameHead", cellClass: "nameRow", render: nameValue };
  }
  const clubLabel = getSetting("clubLabel") ?? "Club";
  return {
    header: [clubLabel, "NAME"],
    headClass: "nameHead",
    cellClass: "nameRow",
    renderParts: (row) => [
      { text: orPlaceholder(row.Competitor), className: "clubValue" },
      { text: nameValue(row), className: "nameValue" },
    ],
  };
}

// Vehicle (Make) and/or Sponsor, individually toggleable ("displayMake",
// server/config-driven, and "showSponsor" via config.json, with a
// configurable "sponsorLabel" alias), stacked vehicle-over-sponsor when both
// are shown - Sponsor always sits in the bottom slot, even for rows with no
// vehicle.
function buildVehicleSponsorColumn() {
  const showVehicle = state.displayMake;
  const showSponsor = getSetting("showSponsor");
  if (!showVehicle && !showSponsor) return [];

  const sponsorLabel = getSetting("sponsorLabel") ?? "Sponsor";

  let header;
  let renderParts;
  if (showVehicle && showSponsor) {
    header = ["FAHRZEUG", sponsorLabel];
    renderParts = (row) => [
      { text: orPlaceholder(row.Make), className: "vehicleValue" },
      { text: orPlaceholder(row.Sponsor), className: "sponsorValue" },
    ];
  } else if (showVehicle) {
    header = "FAHRZEUG";
    renderParts = (row) => [{ text: row.Make || "", className: "vehicleValue" }];
  } else {
    header = sponsorLabel;
    renderParts = (row) => [{ text: row.Sponsor || "", className: "sponsorValue" }];
  }

  return [{ header, headClass: "vehicleSponsorHead", cellClass: "nameRow", renderParts }];
}

// Number of training columns (TRAINING, or 1./2./3. TRAINING if more than
// one) is configurable via config.json "trainingRuns" (default 1) - the
// telegrams already carry PractiseTime1-4/PractisePenalty1-4, only the first
// slot was ever shown.
function buildTrainingColumns() {
  const count = getSetting("trainingRuns") ?? 1;
  const cols = [];
  for (let i = 1; i <= count; i++) {
    const label = count > 1 ? `${i}. TRAINING` : "TRAINING";
    cols.push(timePenaltyCol(label, `PractiseTime${i}`, `PractisePenalty${i}`));
  }
  return cols;
}

function buildModeColumns(slalomMode, tableEntry) {
  switch (slalomMode) {
    case 0:
      return [...buildTrainingColumns(), timePenaltyCol("1. LAUF", "Time1", "Penalty1"), timeCol("Gesamt", "SubTotal1")];
    case 1:
      return [
        ...buildTrainingColumns(),
        timePenaltyCol("1. LAUF", "Time1", "Penalty1"),
        timePenaltyCol("2. LAUF", "Time2", "Penalty2"),
        timeCol("Gesamt", "SubTotal2"),
      ];
    case 2:
      return [
        ...buildTrainingColumns(),
        timePenaltyCol("1. LAUF", "Time1", "Penalty1"),
        timePenaltyCol("2. LAUF", "Time2", "Penalty2"),
        timePenaltyCol("3. LAUF", "Time3", "Penalty3"),
        timeCol("Gesamt", "SubTotal3"),
      ];
    case 3:
      return [
        ...buildTrainingColumns(),
        timePenaltyCol("1. LAUF", "Time1", "Penalty1"),
        timePenaltyCol("2. LAUF", "Time2", "Penalty2"),
        timePenaltyCol("3. LAUF", "Time3", "Penalty3"),
        timePenaltyCol("4. LAUF", "Time4", "Penalty4"),
        timeCol("Gesamt", "SubTotal4"),
      ];
    case 4: {
      const cols = [];
      if (tableEntry.Ranking !== "Ranking2") cols.push(...buildTrainingColumns());
      if (tableEntry.Ranking === "Ranking2") {
        cols.push(
          timePenaltyCol("1. LAUF", "Time3", "Penalty3"),
          timePenaltyCol("2. LAUF", "Time4", "Penalty4"),
          timeCol("Gesamt", "SubTotal34"),
        );
      } else if (["Grid", "ClassGrid"].includes(tableEntry.RankingType)) {
        cols.push(
          timePenaltyCol("1. LAUF", "Time1", "Penalty1"),
          timePenaltyCol("2. LAUF", "Time2", "Penalty2"),
          timePenaltyCol("3. LAUF", "Time3", "Penalty3"),
          timePenaltyCol("4. LAUF", "Time4", "Penalty4"),
        );
      } else {
        cols.push(
          timePenaltyCol("1. LAUF", "Time1", "Penalty1"),
          timePenaltyCol("2. LAUF", "Time2", "Penalty2"),
          timeCol("Gesamt", "SubTotal2"),
        );
      }
      return cols;
    }
    case 10: {
      const cols = [
        timePenaltyCol("TRAINING", "PractiseTime1", "PractisePenalty1"),
        timePenaltyCol("1. LAUF", "Time1", "Penalty1"),
        timePenaltyCol("2. LAUF", "Time2", "Penalty2"),
        timePenaltyCol("3. LAUF", "Time3", "Penalty3"),
      ];
      if (tableEntry.Ranking === "Ranking2") {
        cols.push(penaltyCol("SubTotalPenaltyMT", "Gesamt STRAFE"), timeCol("Gesamt Zeit", "SubTotalMT"));
      } else {
        cols.push(timeCol("Gesamt Zeit", "SubTotal3"));
      }
      return cols;
    }
    default:
      return [];
  }
}

// Scroll tab: a flat, chronological run history (data.Scroll) rather than a
// per-class ranking - same driver/vehicle/sponsor fields as the normal
// ranking, but only ever one Time/Penalty/Run per entry (no Time1-4).
function buildScrollColumns() {
  const cols = [{ header: "NO", headClass: "stnHead", key: "STN", cellClass: "stnRow" }];
  if (state.displayGuest) {
    cols.push({
      header: "G",
      headClass: "guestHead",
      cellClass: "stnRow",
      render: (row) => (row.Guest ? "G" : ""),
    });
  }
  cols.push(buildNameColumn());
  cols.push({ header: "Klasse", headClass: "groupHead", key: "Class", cellClass: "nameRow" });
  if (state.displayGroup) {
    cols.push({ header: "GRUPPE", headClass: "groupHead", key: "Group", cellClass: "nameRow" });
  }
  cols.push(...buildVehicleSponsorColumn());
  cols.push({ header: "LAUF", headClass: "groupHead", key: "Run", cellClass: "nameRow" });
  cols.push(timePenaltyCol("ZEIT", "Time", "Penalty"));
  return cols;
}

// Grid tab: the full per-competitor ranking fields (data.Grid), sorted by
// start number instead of position/class - a quick "look up by STN" list
// across the whole field. Reuses buildModeColumns for the lap columns (with
// a synthetic tableEntry that picks mode 4's "all laps, no aggregate"
// branch, matching what data.Grid already means in that mode), but builds
// NAME/Klasse itself since the normal per-class RankingType gating
// (buildPosColumns/buildNameColumns) doesn't fit a cross-class list. No
// position columns - it's a start list, not a ranking.
function buildGridColumns() {
  const cols = [{ header: "NO", headClass: "stnHead", key: "STN", cellClass: "stnRow" }];
  if (state.displayGuest) {
    cols.push({
      header: "G",
      headClass: "guestHead",
      cellClass: "stnRow",
      render: (row) => (row.Guest ? "G" : ""),
    });
  }
  cols.push(buildNameColumn());
  cols.push({ header: "Klasse", headClass: "groupHead", key: "Class", cellClass: "nameRow" });
  if (state.displayGroup) {
    cols.push({ header: "GRUPPE", headClass: "groupHead", key: "Group", cellClass: "nameRow" });
  }
  cols.push(...buildVehicleSponsorColumn());
  cols.push(...buildModeColumns(state.currentSlalomMode, { Ranking: "Grid", RankingType: "Grid" }));
  return cols;
}

function columnsFor(tableEntry) {
  if (tableEntry.RankingType === "Scroll") return buildScrollColumns();
  if (tableEntry.RankingType === "StartGrid") return buildGridColumns();
  return [
    ...buildPosColumns(tableEntry),
    ...buildNameColumns(tableEntry),
    ...buildModeColumns(state.currentSlalomMode, tableEntry),
  ];
}

function createEvent(eventDiv) {
  const label = document.createElement("p");
  label.textContent = "Veranstaltung: ";
  eventDiv.append(label);
  const value = document.createElement("p");
  value.id = "eventName";
  value.textContent = "-";
  eventDiv.append(value);
}

function setEvent(data) {
  document.getElementById("eventName").innerHTML = data.EventName;
}

function createRanking(rankingdiv, data) {
  state.tableIdentifires = [];
  const tab = document.createElement("div");
  tab.className = "tab";
  rankingdiv.append(tab);

  if (data.SlalomMode === 4) {
    if (getSetting("showRankingAll")) {
      createTab(rankingdiv, tab, "AllV1", data.Classes, "ALL V1", "Ranking1", "ALL", state.newClass === "ALL");
      createTab(rankingdiv, tab, "AllV2", data.Classes, "ALL V2", "Ranking2", "ALL");
      createTab(rankingdiv, tab, "AllALL", data.Classes, "ALL ALL", "Grid", "Grid", false, true);
    }
    for (let i = 0; i < data.Classes.length; i++) {
      const cls = data.Classes[i];
      createTab(rankingdiv, tab, cls + "V1", [cls], cls + " V1", "Ranking1", "Class", state.newClass === cls);
      createTab(rankingdiv, tab, cls + "V2", [cls], cls + " V2", "Ranking2", "Class");
      createTab(rankingdiv, tab, cls + "All", [cls], cls + " ALL", "Grid", "ClassGrid");
    }
  } else if (data.SlalomMode === 10) {
    for (let i = 0; i < data.Classes.length; i++) {
      const cls = data.Classes[i];
      const rank = data.GroupClasses.hasOwnProperty("Jugend") && data.GroupClasses["Jugend"].includes(cls) ? "Ranking2" : "Ranking1";
      createTab(rankingdiv, tab, cls, [cls], cls, rank, "Class", state.newClass === cls);
    }
    for (let i = 0; i < data.Groups.length; i++) {
      const group = data.Groups[i];
      const rank = group === "Jugend" ? "Ranking2" : "Ranking1";
      createTab(rankingdiv, tab, group, data.GroupClasses[group], group, rank, "Group");
    }
  } else {
    if (getSetting("showRankingAll")) {
      createTab(rankingdiv, tab, "All", data.Classes, "ALL", "Ranking1", "ALL", state.newClass === "ALL", true);
    }
    for (let i = 0; i < data.Classes.length; i++) {
      const cls = data.Classes[i];
      createTab(rankingdiv, tab, cls, [cls], cls, "Ranking1", "Class", state.newClass === cls);
    }
  }

  if (state.scrollRequested || getSetting("showScroll")) {
    createTab(rankingdiv, tab, "Scroll", data.Classes, "SCROLL", "Scroll", "Scroll");
  }

  if (state.gridRequested || getSetting("showGrid")) {
    createTab(rankingdiv, tab, "Grid", data.Classes, "GRID", "Grid", "StartGrid");
  }
}

function createRankingHead() {
  for (const tableEntry of state.tableIdentifires) {
    const table = document.getElementById(tableEntry.tableId);
    renderHead(table, columnsFor(tableEntry));
  }
}

function updateRanking(data) {
  for (const tableEntry of state.tableIdentifires) {
    const table = document.getElementById(tableEntry.tableId);
    if (tableEntry.RankingType === "Scroll") {
      // Flat run history, newest first (data.Scroll arrives oldest-first),
      // not filtered by class, and not keyed by STN (the same driver appears
      // once per run). The server only ever appends, so a display row's
      // stable key is its original (pre-reverse) index, not its display
      // position - otherwise every row's identity would shift on each new
      // entry, defeating the in-place update.
      const scrollEntries = data.Scroll ?? [];
      const displayRows = scrollEntries.slice().reverse();
      renderRows(
        table,
        columnsFor(tableEntry),
        displayRows,
        (row, displayIndex) => scrollEntries.length - 1 - displayIndex,
        { highlightNew: true }
      );
      continue;
    }
    if (tableEntry.RankingType === "StartGrid") {
      // Full field, sorted by start number (as delivered), not filtered by
      // class - one entry per competitor, so STN is a safe row key here.
      renderRows(table, columnsFor(tableEntry), data.Grid ?? [], "STN");
      continue;
    }
    const ranking = data[tableEntry.Ranking].filter((row) => tableEntry.Classes.includes(row.Class));
    renderRows(table, columnsFor(tableEntry), ranking, "STN");
  }
}

export const slalomHandlers = {
  createEvent,
  setEvent,
  createRanking,
  createRankingHead,
  updateRanking,
};
